import { httpsCallable } from 'firebase/functions';
import { doc, setDoc } from 'firebase/firestore';
import type { User as FirebaseUser } from 'firebase/auth';
import { auth, functions, db } from '@/firebase';
import { TestType } from '@/models/SharedTypes';
import type { RetakeReport } from '@/models/UserTypes';
import type { LicensingAnswers } from '@/features/licensing/types';
import { contextProfileFill } from '@/utils/learnerContext';
import { UserSegment, AcquisitionChannel, UTMParams } from '@/types/user';
import { logger } from '@/utils/logger';
import { trackReferralForUser } from '@/utils/referralTracking';
import { logAnalyticsEvent, ANALYTICS_EVENTS } from '@/utils/Analytics';
import { trackRedditEvent } from '@/utils/redditPixel';
import { trialUserDiagnosticFields } from '@/utils/onboardingDiagnosticPayload';
import { attachHandoffOnSignup, buildHandoffPayload } from '@/features/publicDiagnostic/session';
import { callWithTimeout, CallTimeoutError } from '@/utils/callWithTimeout';
import { authBreadcrumb, authError } from '@/utils/authObservability';
import type { PendingSocialSignup } from '@/utils/pendingSocialSignup';

// Thrown when the trial could NOT be provisioned (createTrialUser callable failed
// AND the retry failed). The client can't self-grant a trial — firestore.rules
// blocks the privileged fields — so callers must treat this as a hard failure:
// surface the message, do NOT fire success analytics, do NOT route the user in.
export class TrialProvisioningError extends Error {
  readonly reason?: unknown;
  constructor(reason?: unknown) {
    super("We couldn't set up your free account. Please try again in a moment.");
    this.name = 'TrialProvisioningError';
    this.reason = reason;
  }
}

export interface TrialSignupData {
  email: string;
  termsAccepted: boolean;
  marketingConsent: boolean;
  examType?: TestType;
  signupSource: 'email' | 'google' | 'apple';
  userSegment?: UserSegment;
  acquisitionChannel?: AcquisitionChannel;
  utmParams?: UTMParams;
  /**
   * ASWB school code, captured from a /programs CTA's `?school_id=`. Sent as a
   * top-level field; createTrialUser checks it against its own allowlist and
   * ignores an id it does not know, so a stale or hand-typed code never blocks
   * a signup.
   */
  schoolId?: string;
  isRetaker?: boolean;
}

/** The /onboarding personalization, reduced to the fields createTrialUser accepts. */
export type PersonalizationPayload = {
  name?: string;
  examDate?: string;
  retakeReport?: RetakeReport;
} & LicensingAnswers;

const createTrialUser = (payload: Record<string, unknown>) =>
  httpsCallable(functions, 'createTrialUser')(withLearnerContext(payload));

/** The door's school fills a payload that has none. */
export function withLearnerContext(payload: Record<string, unknown>): Record<string, unknown> {
  const { schoolCode } = contextProfileFill({ schoolCode: payload.schoolCode || payload.schoolId });
  return schoolCode ? { ...payload, schoolId: schoolCode } : payload;
}

// App Check reCAPTCHA can HANG the createTrialUser callable indefinitely in
// privacy/adblock/in-app browsers, leaving the spinner up forever. Bound every
// invocation with a hard timeout. `retries: 0` — this is a mutation, so a
// timed-out call may have provisioned server-side; retrying risks a double
// grant. On timeout we reject with CallTimeoutError → the caller's
// catch/fallback runs and the spinner clears with a friendly message.
const TRIAL_TIMEOUT_MS = 15000;

/** Six-turn public diagnostic (#580): persist anon results onto the new account. */
export async function claimPublicDiagnosticHandoff(uid: string): Promise<void> {
  try {
    const payload = buildHandoffPayload();
    if (payload.results.length === 0) return;
    await attachHandoffOnSignup(uid, payload);
  } catch (err) {
    logger.warn('TrialSignupFlow', 'public diagnostic handoff failed (non-fatal)', { err });
  }
}

export async function provisionTrialWithTimeout(
  payload: Record<string, unknown>,
  source: 'email' | 'google' | 'apple'
): Promise<void> {
  authBreadcrumb('trial-callable-start', { source });
  try {
    await callWithTimeout(() => createTrialUser(payload), {
      timeoutMs: TRIAL_TIMEOUT_MS,
      retries: 0,
    });
    authBreadcrumb('trial-callable-success', { source });
  } catch (err) {
    const timedOut = err instanceof CallTimeoutError;
    authError(timedOut ? 'trial-callable-timeout' : 'trial-callable-error', err, { source });
    throw err;
  }
}

export async function createTrialUserFallback(
  userId: string,
  data: TrialSignupData,
  personalizationPayload: PersonalizationPayload = {}
): Promise<void> {
  // SECURITY: the trial grant (subscriptionStatus / trialStartDate /
  // trialEndDate / isTrialActive) is server-only — firestore.rules blocks the
  // client from writing those fields. So this fallback must NOT self-provision a
  // trial. Instead:
  //   1. RETRY the createTrialUser callable — the only legitimate provisioner.
  //      This covers the common transient-error case that triggers the fallback.
  //   2. If the retry still fails, persist ONLY non-privileged profile fields so
  //      the account isn't dataless, then THROW so callers know the trial was
  //      NOT provisioned — they must surface an error, not fake success.
  let retryError: unknown;
  try {
    // Bounded retry — the retry callable can hang exactly like the first call.
    await callWithTimeout(
      () =>
        createTrialUser({
          email: data.email,
          termsAccepted: true,
          marketingConsent: data.marketingConsent,
          examType: data.examType,
          signupSource: data.signupSource,
          userSegment: data.userSegment,
          acquisitionChannel: data.acquisitionChannel,
          utmParams: data.utmParams,
          ...(data.schoolId ? { schoolId: data.schoolId } : {}),
          isRetaker: data.isRetaker,
          ...trialUserDiagnosticFields(),
          ...personalizationPayload,
        }),
      { timeoutMs: TRIAL_TIMEOUT_MS, retries: 0 }
    );
    logger.info('TrialSignupFlow', 'Trial provisioned via callable retry', { userId, email: data.email });
    authBreadcrumb('trial-callable-retry-success', { source: data.signupSource });
    return;
  } catch (err) {
    retryError = err;
    logger.error(
      'TrialSignupFlow',
      'createTrialUser callable failed twice — writing non-privileged profile only; trial NOT provisioned client-side',
      { userId, email: data.email, error: err }
    );
  }

  const now = new Date();

  // NOTE: intentionally OMITS subscriptionStatus / trialStartDate / trialEndDate
  // / isTrialActive — those are server-only and rejected by firestore.rules.
  // merge:true so a later server-side provisioning can add the trial grant
  // without clobbering this profile.
  const userDoc = {
    id: userId,
    email: data.email,
    signupSource: data.signupSource,
    day1ActivationEmailSent: false,
    trialProgressEmailSent: false,
    consent: {
      marketingEmailConsent: data.marketingConsent,
      transactionalEmailConsent: true,
      termsAcceptedAt: now.toISOString(),
      lastUpdated: now.toISOString()
    },
    profile: {
      email: data.email,
      name: '',
      studyGoal: '',
      targetExam: data.examType,
      onboardingCompleted: false,
      emailConsent: data.marketingConsent,
      marketingEmailConsent: data.marketingConsent,
      emailConsentData: {
        emailConsent: data.marketingConsent,
        consentDate: now.toISOString(),
        consentVersion: '1.0',
        userAgent: navigator.userAgent
      },
      termsAcceptedAt: now.toISOString(),
      lastUpdated: now.toISOString()
    },
    quizProgress: null,
    testResults: {},
    wrongAnswers: [],
    totalQuestionsAttempted: 0,
    correctAnswers: 0,
    studyStreak: 0,
    masteryLevel: 0,
    averageScore: 0,
    completedTests: 0,
    totalStudyHours: 0,
    totalWrongAnswers: 0,
    adaptiveLearning: {
      abilityScore: 50,
      confidenceLevel: 0.5,
      lastUpdated: now.toISOString(),
      performanceHistory: [],
      categoryStrengths: {}
    },
    createdAt: now.toISOString(),
    lastActive: now.toISOString()
  };

  await setDoc(doc(db, 'users', userId), userDoc, { merge: true });
  logger.warn(
    'TrialSignupFlow',
    'Wrote non-privileged profile after trial-provisioning failure (no trial grant set client-side)',
    { userId, email: data.email }
  );
  // Sign the visitor out before signalling failure. The account exists but has
  // NO trial, and useRedirectAuthenticatedUser would otherwise sweep the now-
  // authenticated user straight to the dashboard trial-less (e.g. a disposable
  // email createTrialUser rejects). Signed out, they land back on the signup
  // screen with the error and can retry. The non-privileged profile doc above
  // stays for a possible later server-side provisioning.
  // try/catch (not .catch) so a synchronous throw is also swallowed — the
  // trial-provisioning failure below is the error the visitor must see, never
  // a sign-out hiccup.
  try {
    await auth.signOut();
  } catch (signOutErr) {
    logger.warn('TrialSignupFlow', 'signOut after trial-provisioning failure failed (non-fatal)', { userId, error: signOutErr });
  }
  // Signal hard failure: the account exists but has NO trial. Callers must not
  // report success or route the user in as a trial user.
  throw new TrialProvisioningError(retryError);
}

/**
 * Everything after auth for a new signup: create the trial, claim the six turns,
 * log analytics, track referral. Throws when the trial could not be provisioned.
 */
export async function completeTrialSignup(
  user: FirebaseUser,
  ctx: Omit<PendingSocialSignup, 'provider'>,
  provider: 'google' | 'apple' | 'email',
  personalizationPayload: PersonalizationPayload = {}
): Promise<void> {
  const userEmail = user.email;
  if (!userEmail) throw new Error('No email found after social auth');

  logAnalyticsEvent(ANALYTICS_EVENTS.SIGN_UP_STARTED, {
    signup_method: provider,
    user_segment: ctx.userSegment,
    acquisition_channel: ctx.acquisitionChannel,
  });

  const completedEvent = {
    signup_method: provider,
    user_segment: ctx.userSegment,
    acquisition_channel: ctx.acquisitionChannel,
    exam_type: ctx.examType,
    timestamp: new Date().toISOString(),
  };

  try {
    await provisionTrialWithTimeout({
      email: userEmail,
      termsAccepted: true,
      marketingConsent: ctx.marketingConsent,
      examType: ctx.examType,
      signupSource: provider,
      userSegment: ctx.userSegment,
      acquisitionChannel: ctx.acquisitionChannel,
      utmParams: ctx.utmParams,
      ...(ctx.schoolId ? { schoolId: ctx.schoolId } : {}),
      isRetaker: ctx.isRetaker,
      ...(ctx.retakeReport ? { retakeReport: ctx.retakeReport } : {}),
      ...trialUserDiagnosticFields({ name: user.displayName }),
      ...personalizationPayload,
    }, provider);
    await claimPublicDiagnosticHandoff(user.uid);
    logAnalyticsEvent(ANALYTICS_EVENTS.TRIAL_SIGNUP_COMPLETED, completedEvent);
  } catch (functionError) {
    console.error(`createTrialUser function error (${provider}), using fallback:`, functionError);
    await createTrialUserFallback(user.uid, {
      email: userEmail,
      termsAccepted: true,
      marketingConsent: ctx.marketingConsent,
      examType: ctx.examType,
      signupSource: provider,
      userSegment: ctx.userSegment,
      acquisitionChannel: ctx.acquisitionChannel,
      utmParams: ctx.utmParams,
      schoolId: ctx.schoolId,
      isRetaker: ctx.isRetaker,
    }, personalizationPayload);
    await claimPublicDiagnosticHandoff(user.uid);
    logAnalyticsEvent(ANALYTICS_EVENTS.TRIAL_SIGNUP_COMPLETED, completedEvent);
  }

  trackReferralForUser(user.uid).catch((refError) => {
    logger.warn('TrialSignupFlow', 'Referral tracking failed (non-critical)', { error: refError });
  });

  // Ad-platform SignUp conversion. Reached only after a trial was actually
  // provisioned (direct or fallback-retry) - createTrialUserFallback throws
  // on real failure, which skips this whole tail. No-op for internal
  // traffic / non-prod builds (guarded inside trackRedditEvent). Wrapped so
  // a third-party pixel error can't block the caller after the trial is
  // already provisioned.
  try {
    trackRedditEvent('SignUp');
  } catch (trackErr) {
    console.error('SignUp conversion tracking failed (non-fatal):', trackErr);
  }
}
